import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

let ffmpeg: FFmpeg | null = null;
let isLoading = false;
let isProcessing = false;
let loadingPromise: Promise<FFmpeg> | null = null;

async function getFFmpeg(): Promise<FFmpeg> {
  if (loadingPromise) {
    return loadingPromise;
  }

  if (ffmpeg) {
    try {
      if (typeof ffmpeg.writeFile === 'function' && typeof ffmpeg.readFile === 'function') {
        console.log('✅ Existing FFmpeg instance appears valid');
        return ffmpeg;
      }
    } catch (err) {
      console.warn('⚠️ FFmpeg instance check failed, recreating...');
    }
    
    try {
      ffmpeg.terminate();
    } catch {}
    ffmpeg = null;
  }

  isLoading = true;
  loadingPromise = (async () => {
    try {
      console.log('🔧 Loading FFmpeg from LOCAL files...');

      const instance = new FFmpeg();
      ffmpeg = instance;

      instance.on('log', ({ message }) => {
        if (message.includes('error') || message.includes('Error')) {
          console.error('FFmpeg Error:', message);
        }
      });

      await instance.load({
        coreURL: new URL('/ffmpeg/ffmpeg-core.js', window.location.origin).href,
        wasmURL: new URL('/ffmpeg/ffmpeg-core.wasm', window.location.origin).href,
        log: false,
      });

      console.log('🔍 Verifying FFmpeg functionality...');
      await instance.writeFile('verify.txt', new Uint8Array([116, 101, 115, 116]));
      const verifyFile = await instance.readFile('verify.txt');
      await instance.deleteFile('verify.txt');

      if (!verifyFile || verifyFile.length === 0) {
        throw new Error('FFmpeg verification failed');
      }

      console.log('✅ FFmpeg loaded and verified successfully!');
      return instance;
    } catch (error: any) {
      console.error('❌ FFmpeg loading failed:', error);
      ffmpeg = null;

      if (error.message?.includes('404')) {
        throw new Error('FFmpeg files not found. Please run: npm run setup-ffmpeg');
      }

      if (error.message?.includes('SharedArrayBuffer')) {
        throw new Error('Browser does not support SharedArrayBuffer. Please use Chrome/Edge or upload a video file instead.');
      }

      throw new Error(`Failed to load FFmpeg: ${error.message}`);
    } finally {
      isLoading = false;
      loadingPromise = null;
    }
  })();

  return loadingPromise;
}

async function validateAndFetchFile(input: string | File, type: 'audio' | 'image'): Promise<Uint8Array> {
  try {
    let fileData: Uint8Array;
    let fileName: string;
    let fileSize: number;

    if (input instanceof File) {
      fileName = input.name;
      fileSize = input.size;
      
      if (type === 'audio' && !input.type.startsWith('audio/')) {
        throw new Error(`Invalid audio file type: ${input.type}. Please use MP3, WAV, or other audio formats.`);
      }
      if (type === 'image' && !input.type.startsWith('image/')) {
        throw new Error(`Invalid image file type: ${input.type}. Please use JPG, PNG, or other image formats.`);
      }
      
      if (fileSize > 150 * 1024 * 1024) {
        throw new Error(`File too large: ${(fileSize / 1024 / 1024).toFixed(1)}MB. Max 150MB.`);
      }
      
      if (fileSize === 0) {
        throw new Error(`File is empty: ${fileName}`);
      }
      
      console.log(`📁 Processing ${type} file: ${fileName} (${(fileSize / 1024 / 1024).toFixed(2)}MB)`);
      fileData = await fetchFile(input);
      
    } else {
      console.log(`📁 Fetching ${type} from URL: ${input.substring(0, 50)}...`);
      
      const response = await fetch(input);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${type}: ${response.status} ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (type === 'audio' && contentType && !contentType.startsWith('audio/')) {
        throw new Error(`Invalid ${type} content type from URL: ${contentType}`);
      }
      if (type === 'image' && contentType && !contentType.startsWith('image/')) {
        throw new Error(`Invalid ${type} content type from URL: ${contentType}`);
      }
      
      const buffer = await response.arrayBuffer();
      fileData = new Uint8Array(buffer);
      fileSize = fileData.length;
      
      if (fileSize === 0) {
        throw new Error(`Empty ${type} file from URL`);
      }
      
      console.log(`✅ ${type} fetched successfully (${(fileSize / 1024 / 1024).toFixed(2)}MB)`);
    }

    if (!fileData || fileData.length === 0) {
      throw new Error(`Failed to read ${type} file data`);
    }

    return fileData;
    
  } catch (error: any) {
    console.error(`❌ Error processing ${type} file:`, error);
    throw new Error(`${type} file error: ${error.message}`);
  }
}

export async function convertToVideo(
  audio: string | File,
  image: string | File,
  onProgress?: (progress: number) => void
): Promise<Blob> {
  
  if (isProcessing) {
    throw new Error('Another conversion is already in progress. Please wait for it to complete.');
  }
  
  isProcessing = true;
  const startTime = performance.now();
  let ffmpegInstance: FFmpeg | null = null;

  try {
    console.log('🚀 Starting video conversion...');
    
    ffmpegInstance = await getFFmpeg();

    let lastProgress = 0;
    if (onProgress) {
      ffmpegInstance.on('progress', ({ progress }) => {
        const currentProgress = Math.min(Math.round(progress * 100), 100);
        if (currentProgress > lastProgress + 1 || currentProgress >= 100) {
          console.log(`🎬 FFmpeg Progress: ${currentProgress}%`);
          onProgress(currentProgress);
          lastProgress = currentProgress;
        }
      });
    }

    console.log('📁 Validating and loading files...');
    const [audioData, imageData] = await Promise.all([
      validateAndFetchFile(audio, 'audio'),
      validateAndFetchFile(image, 'image'),
    ]);

    const filesToClean = ['audio.mp3', 'image.jpg', 'output.mp4'];
    await Promise.allSettled(
      filesToClean.map(file => ffmpegInstance!.deleteFile(file).catch(() => {}))
    );

    console.log('📝 Writing input files to FFmpeg...');
    await Promise.all([
      ffmpegInstance.writeFile('audio.mp3', audioData),
      ffmpegInstance.writeFile('image.jpg', imageData),
    ]);

    const audioCheck = await ffmpegInstance.readFile('audio.mp3');
    const imageCheck = await ffmpegInstance.readFile('image.jpg');
    
    if (!audioCheck || audioCheck.length === 0) {
      throw new Error('Failed to write audio file to FFmpeg');
    }
    if (!imageCheck || imageCheck.length === 0) {
      throw new Error('Failed to write image file to FFmpeg');
    }
    
    console.log('✅ Input files written successfully');

    console.log('🎬 Starting FFmpeg conversion...');
    await ffmpegInstance.exec([
      '-loop', '1',
      '-i', 'image.jpg',
      '-i', 'audio.mp3',
      '-preset', 'ultrafast',
      '-tune', 'stillimage',
      '-threads', '0',
      '-c:v', 'libx264',
      '-profile:v', 'main',
      '-level', '4.0',
      '-pix_fmt', 'yuv420p',
      '-crf', '28',
      '-g', '30',
      '-bf', '0',
      '-refs', '1',
      '-c:a', 'aac',
      '-b:a', '128k',
      '-ar', '44100',
      '-ac', '2',
      '-vf', 'scale=1280:720:force_original_aspect_ratio=decrease:flags=fast_bilinear,pad=1280:720:(ow-iw)/2:(oh-ih)/2,fps=1',
      '-shortest',
      '-movflags', '+faststart',
      '-y',
      'output.mp4',
    ]);

    console.log('📤 Reading conversion output...');
    const outputData = await ffmpegInstance.readFile('output.mp4');
    
    if (!outputData || !(outputData instanceof Uint8Array)) {
      throw new Error('FFmpeg did not produce valid output data');
    }
    
    if (outputData.length === 0) {
      throw new Error('FFmpeg produced empty output file');
    }

    const conversionTime = ((performance.now() - startTime) / 1000).toFixed(1);
    const outputSizeMB = (outputData.length / 1024 / 1024).toFixed(2);
    
    console.log(`✅ Conversion completed successfully in ${conversionTime}s`);
    console.log(`📦 Output size: ${outputSizeMB}MB`);

    await cleanupFiles(ffmpegInstance, ['audio.mp3', 'image.jpg', 'output.mp4']);
    
    const blob = new Blob([outputData], { type: 'video/mp4' });
    
    if (blob.size === 0) {
      throw new Error('Created blob is empty');
    }
    
    console.log(`🎉 Video conversion successful! Created ${(blob.size / 1024 / 1024).toFixed(2)}MB video`);
    
    return blob;
    
  } catch (error: any) {
    console.error('❌ Conversion failed:', {
      error: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });
    
    if (error.message?.includes('FFmpeg files not found')) {
      throw new Error('FFmpeg setup incomplete. Please upload a video file instead.');
    }
    
    if (error.message?.includes('SharedArrayBuffer')) {
      throw new Error('Browser compatibility issue. Please use Chrome/Edge or upload a video file instead.');
    }
    
    if (error.message?.includes('File too large')) {
      throw error;
    }
    
    if (error.message?.includes('Invalid') && error.message?.includes('file type')) {
      throw error;
    }
    
    if (error.message?.includes('Another conversion is already in progress')) {
      throw error;
    }
    
    throw new Error(`Video conversion failed: ${error.message || 'Unknown error occurred'}`);
    
  } finally {
    isProcessing = false;
    
    if (ffmpegInstance) {
      try {
        await cleanupFiles(ffmpegInstance, ['audio.mp3', 'image.jpg', 'output.mp4']);
      } catch (cleanupErr) {
        console.warn('⚠️ Cleanup warning:', cleanupErr);
      }
    }
  }
}

async function cleanupFiles(ffmpeg: FFmpeg, files: string[]): Promise<void> {
  const results = await Promise.allSettled(
    files.map(async (file) => {
      try {
        await ffmpeg.deleteFile(file);
        console.log(`🧹 Cleaned up ${file}`);
      } catch (err) {
        console.log(`🧹 ${file} already cleaned or doesn't exist`);
      }
    })
  );
  
  const failures = results.filter(r => r.status === 'rejected');
  if (failures.length > 0) {
    console.warn(`⚠️ Some cleanup operations failed, but this is usually not critical`);
  }
}

export async function preloadFFmpeg(): Promise<void> {
  try {
    console.log('🔥 Pre-loading FFmpeg...');
    await getFFmpeg();
    console.log('✅ FFmpeg preloaded and verified successfully');
  } catch (error: any) {
    console.warn('⚠️ FFmpeg pre-load failed:', error.message);
    throw error;
  }
}

export function cleanupFFmpegMemory(): void {
  if (ffmpeg && !isProcessing && !isLoading && !loadingPromise) {
    try {
      ffmpeg.terminate();
      ffmpeg = null;
      console.log('🧹 FFmpeg memory cleaned successfully');
    } catch (error) {
      console.warn('⚠️ Cleanup warning:', error);
    }
  } else if (isProcessing) {
    console.warn('⚠️ Cannot cleanup FFmpeg while processing');
  }
}

export function estimateConversionTime(audioFile: File): string {
  const sizeMB = audioFile.size / 1024 / 1024;
  const estimatedSeconds = Math.max(5, Math.round(sizeMB * 1));
  return estimatedSeconds < 60
    ? `${estimatedSeconds} seconds`
    : `${Math.round(estimatedSeconds / 60)} minute${estimatedSeconds > 60 ? 's' : ''}`;
}

export function checkBrowserSupport(): { supported: boolean; issues: string[] } {
  const issues: string[] = [];

  if (typeof SharedArrayBuffer === 'undefined') {
    issues.push('SharedArrayBuffer not available');
  }

  if (typeof WebAssembly === 'undefined') {
    issues.push('WebAssembly not supported');
  }

  if (typeof window !== 'undefined' && !window.isSecureContext) {
    issues.push('Secure context required (HTTPS)');
  }

  return {
    supported: issues.length === 0,
    issues,
  };
}